import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  ReferenceLine,
} from 'recharts';
import {
  RefreshCw,
  Clock,
  TrendingUp,
  DollarSign,
  Calendar,
  AlertCircle,
  Activity,
  Loader2,
  ArrowLeft,
  ArrowRight,
} from 'lucide-react';
import { useDotyposBusyness } from '@/hooks/integrations/useDotyposBusyness';
import { HOUR_LABELS_24H, DAY_NAMES_SHORT } from '@/types/dotypos-busyness';
import { formatDistanceToNow, format, startOfMonth, endOfMonth, subWeeks, subMonths, subQuarters, subYears, startOfWeek, startOfQuarter, startOfYear, eachDayOfInterval, isSameMonth, isWithinInterval, addMonths, getDay, endOfWeek, endOfQuarter, endOfYear } from 'date-fns';
import { supabase } from '@/integrations/supabase/client';
import { useHolidayData } from '@/hooks/scheduling/useHolidays';
import { BusinessOpeningHour } from '@/types/index';

interface BusynessAnalyticsProps {
  businessId: string;
}

type DateRangeMode = 'month' | 'custom' | 'preset' | 'range';
type PresetRange = 'last_week' | 'last_month' | 'last_quarter' | 'last_year' | 'this_week' | 'this_month' | 'this_quarter' | 'this_year';

const BusynessAnalytics: React.FC<BusynessAnalyticsProps> = ({ businessId }) => {
  const [selectedMonth, setSelectedMonth] = useState(new Date());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [rangeMode, setRangeMode] = useState<DateRangeMode>('month');
  const [customDays, setCustomDays] = useState(30);
  const [presetRange, setPresetRange] = useState<PresetRange>('last_month');

  // Custom range selection state
  const [customRangeStart, setCustomRangeStart] = useState<string | null>(null);
  const [customRangeEnd, setCustomRangeEnd] = useState<string | null>(null);
  const [isSelectingRange, setIsSelectingRange] = useState(false);

  // Business details state (for opening hours and country code)
  const [openingHours, setOpeningHours] = useState<BusinessOpeningHour[]>([]);
  const [countryCode, setCountryCode] = useState<string | null>(null);
  const [loadingBusinessDetails, setLoadingBusinessDetails] = useState(false);

  // Fetch business details (opening hours and country code)
  useEffect(() => {
    const fetchBusinessDetails = async () => {
      if (!businessId) return;

      setLoadingBusinessDetails(true);
      try {
        const { data, error } = await supabase
          .from('businesses')
          .select(`
            country_code,
            business_opening_hours (
              id,
              business_id,
              day_of_week,
              opening_time,
              closing_time,
              is_closed_all_day,
              is_24_hours,
              is_open
            )
          `)
          .eq('id', businessId)
          .single();

        if (error) {
          console.error('Error fetching business details:', error);
          return;
        }

        if (data) {
          setCountryCode(data.country_code || null);
          setOpeningHours(data.business_opening_hours || []);
        }
      } catch (err) {
        console.error('Error fetching business details:', err);
      } finally {
        setLoadingBusinessDetails(false);
      }
    };

    fetchBusinessDetails();
  }, [businessId]);

  // Get years for holiday fetching (based on selected year in calendar)
  const holidayYears = useMemo(() => {
    return [selectedYear - 1, selectedYear, selectedYear + 1];
  }, [selectedYear]);

  // Fetch holidays for the business country
  const { holidays, isHoliday } = useHolidayData({
    countryCode,
    years: holidayYears,
    enabled: !!countryCode
  });

  // Helper to check if a day of week is closed
  const isDayOfWeekClosed = useMemo(() => {
    const closedDaysMap: Record<string, boolean> = {};
    const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

    dayNames.forEach(dayName => {
      const dayHours = openingHours.find(oh => oh.day_of_week === dayName);
      closedDaysMap[dayName] = dayHours?.is_closed_all_day === true;
    });

    return closedDaysMap;
  }, [openingHours]);

  // Helper to get opening/closing hours for charts
  // Only returns hours if ALL open days have the exact same opening and closing times
  // Closed days are ignored, but 24-hour days must match too
  const getTypicalBusinessHours = useMemo(() => {
    // Get all days that are open (not closed all day)
    const openDays = openingHours.filter(oh => !oh.is_closed_all_day);

    if (openDays.length === 0) {
      return { openingHour: null, closingHour: null };
    }

    // Check if any day is 24 hours
    const has24HourDay = openDays.some(oh => oh.is_24_hours === true);

    // Get days with regular hours (not 24h)
    const regularHoursDays = openDays.filter(oh =>
      !oh.is_24_hours &&
      oh.opening_time &&
      oh.closing_time
    );

    // If we have a mix of 24h and regular hours, don't show reference lines
    if (has24HourDay && regularHoursDays.length > 0) {
      return { openingHour: null, closingHour: null };
    }

    // If all open days are 24 hours, don't show reference lines (no open/close times)
    if (has24HourDay && regularHoursDays.length === 0) {
      return { openingHour: null, closingHour: null };
    }

    // Check if all regular hours days have the same opening and closing times
    if (regularHoursDays.length === 0) {
      return { openingHour: null, closingHour: null };
    }

    const firstOpeningTime = regularHoursDays[0].opening_time!;
    const firstClosingTime = regularHoursDays[0].closing_time!;

    const allSameHours = regularHoursDays.every(oh =>
      oh.opening_time === firstOpeningTime && oh.closing_time === firstClosingTime
    );

    if (!allSameHours) {
      // Different hours on different days - don't show reference lines
      return { openingHour: null, closingHour: null };
    }

    // All open days have the same hours - show reference lines
    const openingHour = parseInt(firstOpeningTime.split(':')[0], 10);
    const closingHour = parseInt(firstClosingTime.split(':')[0], 10);

    return { openingHour, closingHour };
  }, [openingHours]);

  // Calculate date range based on mode
  const getDateRange = () => {
    if (rangeMode === 'range' && customRangeStart && customRangeEnd) {
      return {
        from: customRangeStart,
        to: customRangeEnd,
      };
    } else if (rangeMode === 'month') {
      return {
        from: format(startOfMonth(selectedMonth), 'yyyy-MM-dd'),
        to: format(endOfMonth(selectedMonth), 'yyyy-MM-dd'),
      };
    } else if (rangeMode === 'preset') {
      const now = new Date();
      let from: Date;
      let to: Date = now;

      switch (presetRange) {
        case 'this_week':
          from = startOfWeek(now, { weekStartsOn: 1 });
          to = endOfWeek(now, { weekStartsOn: 1 });
          break;
        case 'last_week':
          from = startOfWeek(subWeeks(now, 1), { weekStartsOn: 1 });
          to = new Date(from);
          to.setDate(to.getDate() + 6);
          break;
        case 'this_month':
          from = startOfMonth(now);
          to = endOfMonth(now);
          break;
        case 'last_month':
          from = startOfMonth(subMonths(now, 1));
          to = endOfMonth(subMonths(now, 1));
          break;
        case 'this_quarter':
          from = startOfQuarter(now);
          to = endOfQuarter(now);
          break;
        case 'last_quarter':
          from = startOfQuarter(subQuarters(now, 1));
          to = new Date(from);
          to.setMonth(to.getMonth() + 3);
          to.setDate(to.getDate() - 1);
          break;
        case 'this_year':
          from = startOfYear(now);
          to = endOfYear(now);
          break;
        case 'last_year':
          from = startOfYear(subYears(now, 1));
          to = new Date(from);
          to.setFullYear(to.getFullYear() + 1);
          to.setDate(to.getDate() - 1);
          break;
        default:
          from = subMonths(now, 1);
      }

      return {
        from: format(from, 'yyyy-MM-dd'),
        to: format(to, 'yyyy-MM-dd'),
      };
    } else {
      const to = new Date();
      const from = new Date();
      from.setDate(from.getDate() - customDays);
      return {
        from: format(from, 'yyyy-MM-dd'),
        to: format(to, 'yyyy-MM-dd'),
      };
    }
  };

  const { from: dateFrom, to: dateTo } = getDateRange();

  const {
    busynessStats,
    lastSync,
    isDotyposConfigured,
    isLoading,
    isFetching,
    isPlaceholderData,
    isSyncing,
    syncOrders,
    refetch,
  } = useDotyposBusyness({ businessId, dateFrom, dateTo });

  // Update selected year when month changes
  useEffect(() => {
    setSelectedYear(selectedMonth.getFullYear());
  }, [selectedMonth]);

  const handleMonthSelect = (month: number) => {
    const newDate = new Date(selectedYear, month, 1);
    setSelectedMonth(newDate);
    setRangeMode('month');
  };

  const handleYearChange = (increment: number) => {
    setSelectedYear(selectedYear + increment);
  };

  const handleQuickSelect = (days: number) => {
    setCustomDays(days);
    setRangeMode('custom');
    setIsSelectingRange(false);
  };

  const handlePresetSelect = (preset: PresetRange) => {
    setPresetRange(preset);
    setRangeMode('preset');
    setIsSelectingRange(false);
  };

  const goToCurrentMonth = () => {
    const today = new Date();
    setSelectedMonth(today);
    setSelectedYear(today.getFullYear());
    setRangeMode('month');
    setIsSelectingRange(false);
  };

  const handleDayClick = (dayStr: string) => {
    if (!isSelectingRange) {
      // Start new range selection - don't change rangeMode yet!
      setCustomRangeStart(dayStr);
      setCustomRangeEnd(null);
      setIsSelectingRange(true);
    } else {
      // Complete range selection
      if (customRangeStart) {
        const startDate = new Date(customRangeStart);
        const clickedDate = new Date(dayStr);

        let finalStart = customRangeStart;
        let finalEnd = dayStr;

        if (clickedDate < startDate) {
          // Clicked date is before start, swap them
          finalStart = dayStr;
          finalEnd = customRangeStart;
        }

        // Update all state at once to minimize re-renders
        setCustomRangeStart(finalStart);
        setCustomRangeEnd(finalEnd);
        setIsSelectingRange(false);
        setRangeMode('range');
      }
    }
  };

  const cancelRangeSelection = () => {
    setIsSelectingRange(false);
    setCustomRangeStart(null);
    setCustomRangeEnd(null);
  };

  // Get display label for current range
  const getRangeLabel = () => {
    if (rangeMode === 'range') {
      return 'Custom Range';
    } else if (rangeMode === 'month') {
      return format(selectedMonth, 'MMMM yyyy');
    } else if (rangeMode === 'preset') {
      switch (presetRange) {
        case 'this_week': return 'This Week';
        case 'last_week': return 'Last Week';
        case 'this_month': return 'This Month';
        case 'last_month': return 'Last Month';
        case 'this_quarter': return 'This Quarter';
        case 'last_quarter': return 'Last Quarter';
        case 'this_year': return 'This Year';
        case 'last_year': return 'Last Year';
        default: return 'Custom Range';
      }
    } else {
      return `Last ${customDays} days`;
    }
  };

  // Check if month is in the future
  const isMonthDisabled = (month: number, year: number) => {
    const now = new Date();
    const checkDate = new Date(year, month, 1);
    return checkDate > now;
  };

  // Check if year navigation should be disabled
  const isYearDisabled = (year: number, direction: 'previous' | 'next') => {
    if (direction === 'next') {
      return year > new Date().getFullYear();
    }
    return year < 2020; // Reasonable lower bound
  };

  // If Dotypos is not configured
  if (!isDotyposConfigured) {
    return (
      <Alert>
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>Dotypos Integration Required</AlertTitle>
        <AlertDescription>
          To view busyness analytics, please configure your Dotypos POS integration in the
          Settings &gt; Integrations section.
        </AlertDescription>
      </Alert>
    );
  }

  // Loading state
  if (isLoading && !busynessStats) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-full" />
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-32" />
          ))}
        </div>
        <Skeleton className="h-64" />
      </div>
    );
  }

  // Render mini calendar for a month
  const renderMiniCalendar = (month: Date, monthIndex: number) => {
    const monthStart = startOfMonth(month);
    const monthEnd = endOfMonth(month);
    const daysInMonth = eachDayOfInterval({ start: monthStart, end: monthEnd });
    const firstDayOfWeek = getDay(monthStart);
    const startOffset = firstDayOfWeek === 0 ? 6 : firstDayOfWeek - 1;
    const fromDate = new Date(dateFrom);
    const toDate = new Date(dateTo);
    const isSelected = rangeMode === 'month' && selectedMonth.getMonth() === monthIndex && selectedMonth.getFullYear() === selectedYear;
    const today = new Date();

    return (
      <div
        key={month.toISOString()}
        className={`
          rounded-lg p-2 transition-all
          ${isSelected && !isSelectingRange ? 'bg-primary/10 ring-2 ring-primary' : 'bg-muted/30'}
          ${isSelectingRange ? 'ring-2 ring-orange-400' : ''}
        `}
      >
        <div
          className={`text-xs font-medium text-center mb-1 cursor-pointer hover:text-primary ${isSelected && !isSelectingRange ? 'text-primary' : ''}`}
          onClick={() => !isMonthDisabled(monthIndex, selectedYear) && handleMonthSelect(monthIndex)}
        >
          {format(month, 'MMM')}
        </div>
        <div className="grid grid-cols-7 gap-0.5 w-full">
          {/* Day headers */}
          {['M', 'T', 'W', 'T', 'F', 'S', 'S'].map((d, i) => (
            <div key={i} className="text-[9px] text-center text-muted-foreground font-medium">
              {d}
            </div>
          ))}
          {/* Empty cells for offset */}
          {Array.from({ length: startOffset }).map((_, i) => (
            <div key={`empty-${i}`} className="aspect-square" />
          ))}
          {/* Days */}
          {daysInMonth.map((day) => {
            const dayStr = format(day, 'yyyy-MM-dd');
            // During selection, keep showing the current range, don't flicker
            const isInRange = !isSelectingRange && isWithinInterval(day, { start: fromDate, end: toDate });
            const isStartDay = !isSelectingRange && dayStr === dateFrom;
            const isEndDay = !isSelectingRange && dayStr === dateTo;
            const isSelectionStart = dayStr === customRangeStart;
            const isFuture = day > today;
            const isDisabled = isFuture;
            const isToday = format(today, 'yyyy-MM-dd') === dayStr;

            // Check if this day is a holiday
            const holidayInfo = isHoliday(day);
            const isHolidayDay = !!holidayInfo;

            // Check if this day of week is closed
            const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
            const dayName = dayNames[getDay(day)];
            const isClosedDay = isDayOfWeekClosed[dayName] || false;

            return (
              <div
                key={day.toISOString()}
                className={`
                  aspect-square text-[10px] flex items-center justify-center rounded-sm transition-colors
                  ${!isDisabled ? 'cursor-pointer hover:ring-2 hover:ring-primary' : 'opacity-40'}
                  ${isInRange ? 'bg-primary text-primary-foreground' : 'text-foreground'}
                  ${isStartDay || isEndDay ? 'ring-1 ring-primary ring-offset-1' : ''}
                  ${isSelectionStart && isSelectingRange ? 'bg-orange-500 text-white ring-2 ring-orange-400' : ''}
                  ${isToday && !isSelectionStart && !isHolidayDay ? 'ring-2 ring-orange-500' : ''}
                  ${isHolidayDay ? 'ring-2 ring-red-500' : ''}
                  ${isClosedDay && !isInRange ? 'opacity-50' : ''}
                `}
                onClick={(e) => {
                  e.stopPropagation();
                  if (!isDisabled) {
                    handleDayClick(dayStr);
                  }
                }}
                title={`${format(day, 'MMM d, yyyy')}${isHolidayDay ? ` - ${holidayInfo.name}` : ''}${isClosedDay ? ' (Closed)' : ''}${isSelectingRange ? ' - Click to set end date' : ' - Click to start range'}`}
              >
                {format(day, 'd')}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // Render the date selector component (used in multiple places)
  const renderDateSelector = () => (
    <Card>
      <CardContent className="p-4">
        <div className="flex flex-col lg:flex-row gap-6">
          {/* Month/Year Selector with Mini Calendars */}
          <div className="flex-1">
            <div className="flex items-center justify-between mb-3">
              <div className="text-sm font-medium">Select Period</div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="icon"
                  className="h-7 w-7"
                  onClick={() => handleYearChange(-1)}
                  disabled={isYearDisabled(selectedYear - 1, 'previous')}
                >
                  <ArrowLeft className="h-3.5 w-3.5" />
                </Button>
                <span className="text-sm font-semibold min-w-[50px] text-center">{selectedYear}</span>
                <Button
                  variant="outline"
                  size="icon"
                  className="h-7 w-7"
                  onClick={() => handleYearChange(1)}
                  disabled={isYearDisabled(selectedYear + 1, 'next')}
                >
                  <ArrowRight className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>

            {/* Range Selection Mode Indicator */}
            {isSelectingRange && (
              <div className="mb-3 p-2 bg-orange-50 dark:bg-orange-950/30 border border-orange-200 dark:border-orange-800 rounded-lg flex items-center justify-between">
                <div className="text-xs text-orange-700 dark:text-orange-300">
                  <span className="font-medium">Selecting range:</span> Click another day to set end date
                  {customRangeStart && (
                    <span className="ml-1">
                      (Start: {format(new Date(customRangeStart), 'MMM d, yyyy')})
                    </span>
                  )}
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 text-xs text-orange-700 dark:text-orange-300 hover:bg-orange-100 dark:hover:bg-orange-900"
                  onClick={cancelRangeSelection}
                >
                  Cancel
                </Button>
              </div>
            )}

            {/* 4x3 Month Grid with Mini Calendars */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
              {Array.from({ length: 12 }).map((_, index) => {
                const month = new Date(selectedYear, index, 1);
                return renderMiniCalendar(month, index);
              })}
            </div>

            {/* Current month button */}
            <Button
              variant="ghost"
              size="sm"
              className="w-full mt-3"
              onClick={goToCurrentMonth}
            >
              <Calendar className="mr-2 h-4 w-4" />
              Current Month
            </Button>
          </div>

          {/* Divider */}
          <div className="hidden lg:block w-px bg-border" />
          <div className="lg:hidden h-px bg-border" />

          {/* Quick Select */}
          <div className="lg:w-auto">
            <div className="text-sm font-medium mb-3">Quick Select</div>
            <div className="space-y-3">
              {/* Preset ranges - Two columns */}
              <div className="grid grid-cols-2 gap-4">
                {/* Current Period Column */}
                <div>
                  <div className="text-xs text-muted-foreground mb-2 font-medium">Current</div>
                  <div className="space-y-1.5">
                    {[
                      { label: 'Week', preset: 'this_week' as PresetRange },
                      { label: 'Month', preset: 'this_month' as PresetRange },
                      { label: 'Quarter', preset: 'this_quarter' as PresetRange },
                      { label: 'Year', preset: 'this_year' as PresetRange },
                    ].map(({ label, preset }) => (
                      <Button
                        key={preset}
                        variant={rangeMode === 'preset' && presetRange === preset ? "default" : "outline"}
                        size="sm"
                        className="w-full justify-start text-xs"
                        onClick={() => handlePresetSelect(preset)}
                      >
                        {label}
                      </Button>
                    ))}
                  </div>
                </div>

                {/* Previous Period Column */}
                <div>
                  <div className="text-xs text-muted-foreground mb-2 font-medium">Previous</div>
                  <div className="space-y-1.5">
                    {[
                      { label: 'Week', preset: 'last_week' as PresetRange },
                      { label: 'Month', preset: 'last_month' as PresetRange },
                      { label: 'Quarter', preset: 'last_quarter' as PresetRange },
                      { label: 'Year', preset: 'last_year' as PresetRange },
                    ].map(({ label, preset }) => (
                      <Button
                        key={preset}
                        variant={rangeMode === 'preset' && presetRange === preset ? "default" : "outline"}
                        size="sm"
                        className="w-full justify-start text-xs"
                        onClick={() => handlePresetSelect(preset)}
                      >
                        {label}
                      </Button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Day-based ranges */}
              <div className="pt-2 border-t">
                <div className="text-xs text-muted-foreground mb-2 font-medium">Rolling Days</div>
                <div className="flex flex-wrap gap-1.5">
                  {[
                    { label: '7d', days: 7 },
                    { label: '14d', days: 14 },
                    { label: '30d', days: 30 },
                    { label: '60d', days: 60 },
                    { label: '90d', days: 90 },
                  ].map(({ label, days }) => (
                    <Button
                      key={days}
                      variant={rangeMode === 'custom' && customDays === days ? "default" : "outline"}
                      size="sm"
                      className="text-xs px-3"
                      onClick={() => handleQuickSelect(days)}
                    >
                      {label}
                    </Button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Selected Range Display */}
        <div className="mt-4 pt-4 border-t">
          <div className="flex items-center justify-between">
            <div className="text-sm text-muted-foreground">
              <span className="font-medium">Viewing:</span>{' '}
              {getRangeLabel()}
              <span className="ml-2 text-xs">
                ({dateFrom} to {dateTo})
              </span>
              {isFetching && !isLoading && (
                <Loader2 className="inline-block ml-2 h-3 w-3 animate-spin text-primary" />
              )}
              {isPlaceholderData && (
                <Badge variant="outline" className="ml-2 text-xs">
                  Loading...
                </Badge>
              )}
            </div>
            {lastSync && (
              <div className="text-xs text-muted-foreground flex items-center gap-2">
                <Clock className="h-3 w-3" />
                Last sync:{' '}
                {lastSync.completed_at
                  ? formatDistanceToNow(new Date(lastSync.completed_at), { addSuffix: true })
                  : 'Never'}
                {lastSync.orders_synced > 0 && (
                  <Badge className="text-xs">{lastSync.orders_synced} orders</Badge>
                )}
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );

  // No data yet - but keep the selector visible!
  if (!busynessStats) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold">Busyness Analytics</h2>
            <p className="text-muted-foreground">
              Analyze your establishment's traffic patterns
            </p>
          </div>
          <Button onClick={() => syncOrders({})} disabled={isSyncing}>
            {isSyncing ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Syncing...
              </>
            ) : (
              <>
                <RefreshCw className="mr-2 h-4 w-4" />
                Sync Orders
              </>
            )}
          </Button>
        </div>

        {/* Keep date selector visible */}
        {renderDateSelector()}

        <Alert>
          <Activity className="h-4 w-4" />
          <AlertTitle>No Data for Selected Period</AlertTitle>
          <AlertDescription>
            No order data found for {getRangeLabel()}. Try selecting a different period or sync orders from Dotypos.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  // Format currency
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  // Prepare chart data
  const hourlyChartData = busynessStats.hourlyPattern.map((data) => ({
    hour: HOUR_LABELS_24H[data.hour],
    orders: Math.round(data.avgOrders * 10) / 10,
    revenue: Math.round(data.avgRevenue),
  }));

  const dayOfWeekChartData = busynessStats.dayOfWeekPattern.map((data) => ({
    day: DAY_NAMES_SHORT[data.dayOfWeek],
    orders: Math.round(data.avgOrders * 10) / 10,
    revenue: Math.round(data.avgRevenue),
  }));

  // Get top 3 peak hours formatted
  const topPeakHours = busynessStats.peakHours.slice(0, 3).map((peak) => ({
    time: HOUR_LABELS_24H[peak.hour],
    orders: peak.avgOrders.toFixed(1),
  }));

  return (
    <div className="space-y-6">
      {/* Header with controls */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold">Busyness Analytics</h2>
          <p className="text-muted-foreground">
            Analyze your establishment's traffic patterns from Dotypos POS data
          </p>
        </div>

        <Button
          variant="outline"
          onClick={() => syncOrders({})}
          disabled={isSyncing}
        >
          {isSyncing ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Syncing...
            </>
          ) : (
            <>
              <RefreshCw className="mr-2 h-4 w-4" />
              Sync Now
            </>
          )}
        </Button>
      </div>

      {/* Date Range Selector - Always Visible */}
      {renderDateSelector()}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Total Orders
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{busynessStats.totalOrders.toLocaleString()}</div>
            <p className="text-xs text-muted-foreground">
              ~{busynessStats.avgOrdersPerDay.toFixed(1)} per day
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Total Revenue
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatCurrency(busynessStats.totalRevenue)}
            </div>
            <p className="text-xs text-muted-foreground">
              Avg {formatCurrency(busynessStats.avgOrderValue)} per order
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Peak Hour
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {HOUR_LABELS_24H[busynessStats.busiestHour]}
            </div>
            <p className="text-xs text-muted-foreground">
              {busynessStats.peakHours[0]?.avgOrders.toFixed(1)} orders/hour avg
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Busiest Day
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {busynessStats.peakDays[0]?.dayName || 'N/A'}
            </div>
            <p className="text-xs text-muted-foreground">
              {busynessStats.peakDays[0]?.avgOrders.toFixed(1)} orders avg
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Peak Hours List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5" />
            Top Peak Hours
          </CardTitle>
          <CardDescription>When your establishment is busiest</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-3">
            {topPeakHours.map((peak, index) => (
              <Badge
                key={peak.time}
                variant={index === 0 ? 'default' : 'secondary'}
                className="text-sm py-1 px-3"
              >
                #{index + 1} {peak.time} ({peak.orders} orders/hr)
              </Badge>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Orders by Hour */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5" />
              Orders by Hour
            </CardTitle>
            <CardDescription>
              Average orders per hour of day
              {getTypicalBusinessHours.openingHour !== null && (
                <span className="ml-2 text-xs">
                  (Business hours: {HOUR_LABELS_24H[getTypicalBusinessHours.openingHour]} - {HOUR_LABELS_24H[getTypicalBusinessHours.closingHour!]})
                </span>
              )}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={hourlyChartData}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis
                    dataKey="hour"
                    tick={{ fontSize: 10 }}
                    interval={2}
                    angle={-45}
                    textAnchor="end"
                    height={60}
                  />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip
                    content={({ active, payload, label }) => {
                      if (active && payload && payload.length) {
                        return (
                          <div className="bg-background border rounded-lg p-3 shadow-lg">
                            <p className="font-medium">{label}</p>
                            <p className="text-sm text-muted-foreground">
                              Orders: {payload[0].value}
                            </p>
                          </div>
                        );
                      }
                      return null;
                    }}
                  />
                  {/* Opening hour reference line */}
                  {getTypicalBusinessHours.openingHour !== null && (
                    <ReferenceLine
                      x={HOUR_LABELS_24H[getTypicalBusinessHours.openingHour]}
                      stroke="#22c55e"
                      strokeDasharray="5 5"
                      strokeWidth={2}
                      label={{
                        value: 'Open',
                        position: 'top',
                        fill: '#22c55e',
                        fontSize: 10,
                      }}
                    />
                  )}
                  {/* Closing hour reference line */}
                  {getTypicalBusinessHours.closingHour !== null && (
                    <ReferenceLine
                      x={HOUR_LABELS_24H[getTypicalBusinessHours.closingHour]}
                      stroke="#ef4444"
                      strokeDasharray="5 5"
                      strokeWidth={2}
                      label={{
                        value: 'Close',
                        position: 'top',
                        fill: '#ef4444',
                        fontSize: 10,
                      }}
                    />
                  )}
                  <Bar dataKey="orders" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Orders by Day of Week */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              Orders by Day
            </CardTitle>
            <CardDescription>Average orders per day of week</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={dayOfWeekChartData}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis dataKey="day" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip
                    content={({ active, payload, label }) => {
                      if (active && payload && payload.length) {
                        return (
                          <div className="bg-background border rounded-lg p-3 shadow-lg">
                            <p className="font-medium">{label}</p>
                            <p className="text-sm text-muted-foreground">
                              Orders: {payload[0].value}
                            </p>
                          </div>
                        );
                      }
                      return null;
                    }}
                  />
                  <Bar dataKey="orders" fill="hsl(var(--chart-2))" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Revenue by Hour */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <DollarSign className="h-5 w-5" />
              Revenue by Hour
            </CardTitle>
            <CardDescription>
              Average revenue per hour of day
              {getTypicalBusinessHours.openingHour !== null && (
                <span className="ml-2 text-xs">
                  (Business hours: {HOUR_LABELS_24H[getTypicalBusinessHours.openingHour]} - {HOUR_LABELS_24H[getTypicalBusinessHours.closingHour!]})
                </span>
              )}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={hourlyChartData}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis
                    dataKey="hour"
                    tick={{ fontSize: 10 }}
                    interval={2}
                    angle={-45}
                    textAnchor="end"
                    height={60}
                  />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip
                    content={({ active, payload, label }) => {
                      if (active && payload && payload.length) {
                        return (
                          <div className="bg-background border rounded-lg p-3 shadow-lg">
                            <p className="font-medium">{label}</p>
                            <p className="text-sm text-muted-foreground">
                              Revenue: {formatCurrency(payload[0].value as number)}
                            </p>
                          </div>
                        );
                      }
                      return null;
                    }}
                  />
                  {/* Opening hour reference line */}
                  {getTypicalBusinessHours.openingHour !== null && (
                    <ReferenceLine
                      x={HOUR_LABELS_24H[getTypicalBusinessHours.openingHour]}
                      stroke="#22c55e"
                      strokeDasharray="5 5"
                      strokeWidth={2}
                      label={{
                        value: 'Open',
                        position: 'top',
                        fill: '#22c55e',
                        fontSize: 10,
                      }}
                    />
                  )}
                  {/* Closing hour reference line */}
                  {getTypicalBusinessHours.closingHour !== null && (
                    <ReferenceLine
                      x={HOUR_LABELS_24H[getTypicalBusinessHours.closingHour]}
                      stroke="#ef4444"
                      strokeDasharray="5 5"
                      strokeWidth={2}
                      label={{
                        value: 'Close',
                        position: 'top',
                        fill: '#ef4444',
                        fontSize: 10,
                      }}
                    />
                  )}
                  <Line
                    type="monotone"
                    dataKey="revenue"
                    stroke="hsl(var(--chart-3))"
                    strokeWidth={2}
                    dot={{ r: 3 }}
                    activeDot={{ r: 6 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Heatmap */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Activity className="h-5 w-5" />
              Busyness Heatmap
            </CardTitle>
            <CardDescription>
              Order density by hour and day
              {getTypicalBusinessHours.openingHour !== null && (
                <span className="ml-2 text-xs">
                  (Green/red borders show opening/closing hours)
                </span>
              )}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <div className="min-w-[600px]">
                {/* Hour labels */}
                <div className="flex ml-12 mb-1">
                  {[0, 6, 12, 18, 23].map((h) => (
                    <div
                      key={h}
                      className="text-xs text-muted-foreground"
                      style={{ marginLeft: h === 0 ? 0 : `${((h - (h === 6 ? 0 : h === 12 ? 6 : h === 18 ? 12 : 18)) / 24) * 100}%` }}
                    >
                      {HOUR_LABELS_24H[h].split(':')[0]}
                    </div>
                  ))}
                </div>

                {/* Heatmap grid - reordered to start with Monday */}
                {[1, 2, 3, 4, 5, 6, 0].map((dayIndex, rowIndex) => {
                  const dayName = DAY_NAMES_SHORT[dayIndex];
                  // Map day index to full day name for opening hours lookup
                  const fullDayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
                  const fullDayName = fullDayNames[dayIndex];
                  const dayOpeningHours = openingHours.find(oh => oh.day_of_week === fullDayName);
                  const isDayClosed = dayOpeningHours?.is_closed_all_day === true;
                  const is24Hours = dayOpeningHours?.is_24_hours === true;

                  // Get previous day's info to check if it has overnight closing
                  const prevDayOrder = [1, 2, 3, 4, 5, 6, 0];
                  const prevRowIndex = rowIndex === 0 ? 6 : rowIndex - 1;
                  const prevDayIndex = prevDayOrder[prevRowIndex];
                  const prevDayName = fullDayNames[prevDayIndex];
                  const prevDayOpeningHours = openingHours.find(oh => oh.day_of_week === prevDayName);
                  let prevDayCloseHour: number | null = null;
                  let prevDayCloseMinuteOffset = 0;
                  let prevDayIsOvernight = false;

                  if (prevDayOpeningHours && !prevDayOpeningHours.is_closed_all_day && !prevDayOpeningHours.is_24_hours &&
                      prevDayOpeningHours.opening_time && prevDayOpeningHours.closing_time) {
                    const [prevOpenH] = prevDayOpeningHours.opening_time.split(':').map(Number);
                    const [prevCloseH, prevCloseM] = prevDayOpeningHours.closing_time.split(':').map(Number);
                    prevDayCloseHour = prevCloseH;
                    prevDayCloseMinuteOffset = prevCloseM / 60;
                    prevDayIsOvernight = prevCloseH < prevOpenH;
                  }

                  // Get opening and closing hour for this specific day
                  let dayOpenHour: number | null = null;
                  let dayCloseHour: number | null = null;
                  let openMinuteOffset = 0;
                  let closeMinuteOffset = 0;
                  let isOvernight = false;

                  if (dayOpeningHours && !isDayClosed && !is24Hours && dayOpeningHours.opening_time && dayOpeningHours.closing_time) {
                    const [openH, openM] = dayOpeningHours.opening_time.split(':').map(Number);
                    const [closeH, closeM] = dayOpeningHours.closing_time.split(':').map(Number);
                    dayOpenHour = openH;
                    dayCloseHour = closeH;
                    openMinuteOffset = openM / 60; // Convert minutes to fraction of hour (0-1)
                    closeMinuteOffset = closeM / 60;
                    // Check if closing time is before opening time (overnight operation)
                    isOvernight = dayCloseHour < dayOpenHour || (dayCloseHour === dayOpenHour && closeMinuteOffset < openMinuteOffset);
                  }

                  return (
                    <div key={dayName} className="flex items-center gap-2 mb-1">
                      <div className={`w-10 text-xs text-right ${isDayClosed ? 'text-red-500 line-through' : is24Hours ? 'text-blue-500' : 'text-muted-foreground'}`}>
                        {dayName}
                        {is24Hours && <span className="text-[8px] block">24h</span>}
                      </div>
                      <div className="flex-1 flex gap-0.5 relative">
                        {Array.from({ length: 24 }).map((_, hourIndex) => {
                          const cell = busynessStats.heatmapData.find(
                            (c) => c.dayOfWeek === dayIndex && c.hour === hourIndex
                          );
                          const intensity = cell?.intensity || 0;
                          const opacity = Math.max(0.1, intensity);

                          // Determine if hour is outside business hours
                          let isOutsideHours = false;
                          if (!is24Hours && dayOpenHour !== null && dayCloseHour !== null) {
                            if (isOvernight) {
                              // Overnight: open from dayOpenHour to 23:59, then 00:00 to dayCloseHour
                              // Outside hours: from dayCloseHour to dayOpenHour (exclusive)
                              isOutsideHours = hourIndex >= dayCloseHour && hourIndex < dayOpenHour;
                            } else {
                              // Regular hours: open from dayOpenHour to dayCloseHour
                              isOutsideHours = hourIndex < dayOpenHour || hourIndex >= dayCloseHour;
                            }
                          }

                          return (
                            <div
                              key={hourIndex}
                              className="flex-1 h-6 rounded-sm transition-colors"
                              style={{
                                backgroundColor: `hsl(var(--primary) / ${opacity})`,
                              }}
                              title={`${dayName} ${HOUR_LABELS_24H[hourIndex]}: ${cell?.avgOrders.toFixed(1) || 0} orders${isDayClosed ? ' (Closed day)' : is24Hours ? ' (24h open)' : isOutsideHours ? ' (Outside business hours)' : ''}`}
                            />
                          );
                        })}
                        {/* Vertical line for opening time of this day */}
                        {!is24Hours && dayOpenHour !== null && (
                          <div
                            className="absolute top-0 bottom-0 w-0.5 bg-green-500 z-10"
                            style={{
                              left: `calc(${(dayOpenHour + openMinuteOffset) / 24 * 100}% - 1px)`,
                            }}
                            title={`Opens at ${dayOpeningHours?.opening_time}`}
                          />
                        )}
                        {/* Vertical line for closing time - only if NOT overnight (same day close) */}
                        {!is24Hours && dayCloseHour !== null && !isOvernight && (
                          <div
                            className="absolute top-0 bottom-0 w-0.5 bg-red-500 z-10"
                            style={{
                              left: `calc(${(dayCloseHour + closeMinuteOffset) / 24 * 100}% - 1px)`,
                            }}
                            title={`Closes at ${dayOpeningHours?.closing_time}`}
                          />
                        )}
                        {/* Vertical line for closing time from PREVIOUS day's overnight operation */}
                        {prevDayIsOvernight && prevDayCloseHour !== null && (
                          <div
                            className="absolute top-0 bottom-0 w-0.5 bg-red-500 z-10"
                            style={{
                              left: `calc(${(prevDayCloseHour + prevDayCloseMinuteOffset) / 24 * 100}% - 1px)`,
                            }}
                            title={`Closes at ${prevDayOpeningHours?.closing_time} (from ${DAY_NAMES_SHORT[prevDayIndex]})`}
                          />
                        )}
                      </div>
                    </div>
                  );
                })}

                {/* Legend */}
                <div className="flex flex-wrap items-center justify-end gap-4 mt-3">
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground">Less busy</span>
                    <div className="flex gap-0.5">
                      {[0.1, 0.3, 0.5, 0.7, 1].map((opacity) => (
                        <div
                          key={opacity}
                          className="w-4 h-4 rounded-sm"
                          style={{
                            backgroundColor: `hsl(var(--primary) / ${opacity})`,
                          }}
                        />
                      ))}
                    </div>
                    <span className="text-xs text-muted-foreground">More busy</span>
                  </div>
                  {openingHours.length > 0 && (
                    <div className="flex items-center gap-3">
                      <div className="flex items-center gap-1">
                        <div className="w-0.5 h-4 bg-green-500" />
                        <span className="text-xs text-muted-foreground">Open</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <div className="w-0.5 h-4 bg-red-500" />
                        <span className="text-xs text-muted-foreground">Close</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default BusynessAnalytics;
